import React from 'react';
import Link from 'next/link';

/* ---------------------------------------------------------------------------
 * Section sub-components
 *
 * Each covers a logical heading group from the TCPA guide and is defined
 * before the default-export composer so the file reads top-down.
 * --------------------------------------------------------------------------- */

// ── What is the TCPA? ──────────────────────────────────────────────────────

function WhatIsTcpaSection() {
  return (
    <section id="what-is-tcpa" className="mb-12">
      <h2 className="text-2xl font-semibold mb-4 text-text-primary">What is the TCPA?</h2>
      <p className="text-text-secondary mb-4">
        The <strong>Telephone Consumer Protection Act (TCPA)</strong> is a federal law enacted in
        1991 that regulates telemarketing calls, auto-dialed calls, prerecorded calls, text
        messages, and unsolicited faxes. While originally focused on phone calls, the law has been
        interpreted by the FCC and courts to apply to SMS text messaging.
      </p>

      <div className="bg-card-bg border border-border rounded-2xl px-5 py-4 mb-6">
        <h4 className="font-semibold text-text-primary mb-3">Key TCPA Provisions for Texting:</h4>
        <ul className="space-y-2 text-text-secondary">
          <li>
            <strong>Prior consent required:</strong> You must have consent before sending text
            messages to consumers
          </li>
          <li>
            <strong>Written consent for marketing:</strong> Marketing/promotional messages require
            prior express written consent
          </li>
          <li>
            <strong>Opt-out must be honored:</strong> Recipients must be able to opt out, and you
            must honor those requests immediately
          </li>
          <li>
            <strong>Time restrictions:</strong> Messages should not be sent before 8 AM or after 9
            PM in the recipient&apos;s time zone
          </li>
          <li>
            <strong>Identification:</strong> Messages must identify who is sending them
          </li>
        </ul>
      </div>

      <p className="text-text-secondary">
        The TCPA works alongside other regulations like the <strong>CAN-SPAM Act</strong> (for
        email), <strong>state telemarketing laws</strong>, and <strong>carrier policies</strong> to
        create a framework for lawful business communication.
      </p>
    </section>
  );
}

// ── Why TCPA Compliance Matters ─────────────────────────────────────────────

function WhyComplianceMattersSection() {
  return (
    <section id="why-matters" className="mb-12">
      <h2 className="text-2xl font-semibold mb-4 text-text-primary">Why TCPA Compliance Matters</h2>

      <h3 className="text-xl font-semibold mt-6 mb-3 text-text-primary">Financial Risk</h3>
      <p className="text-text-secondary mb-4">TCPA violations carry significant statutory penalties:</p>
      <ul className="list-disc pl-6 text-text-secondary mb-6 space-y-2">
        <li>
          <strong>$500 per violation</strong> for negligent violations (each text to each recipient
          is a separate violation)
        </li>
        <li>
          <strong>$1,500 per violation</strong> for willful or knowing violations (treble damages)
        </li>
        <li>
          <strong>Class action exposure:</strong> TCPA lawsuits are frequently brought as class
          actions, multiplying exposure dramatically
        </li>
      </ul>

      <div className="bg-red-50 border border-red-200 rounded-2xl px-5 py-3 mb-6">
        <p className="text-sm text-red-800 m-0">
          <strong>Example:</strong> A campaign of 1,000 messages sent without proper consent could
          result in $500,000 to $1.5 million in statutory damages — before attorney fees and costs.
        </p>
      </div>

      <h3 className="text-xl font-semibold mt-6 mb-3 text-text-primary">Reputation & Deliverability</h3>
      <ul className="list-disc pl-6 text-text-secondary mb-6 space-y-2">
        <li>Consumer complaints to carriers can result in your number being flagged or blocked</li>
        <li>Poor compliance practices damage client trust and agency reputation</li>
        <li>Repeated violations can lead to your messaging capabilities being suspended</li>
      </ul>

      <h3 className="text-xl font-semibold mt-6 mb-3 text-text-primary">E&O Considerations</h3>
      <p className="text-text-secondary mb-4">
        For insurance agencies, compliance failures can create E&O exposure. If a client misses an
        important policy communication because your texting privileges were suspended, or if
        compliance issues affect your ability to service accounts, it could have professional
        liability implications.
      </p>
    </section>
  );
}

// ── Types of Consent ────────────────────────────────────────────────────────

function ConsentTypesSection() {
  return (
    <section id="consent-types" className="mb-12">
      <h2 className="text-2xl font-semibold mb-4 text-text-primary">Types of Consent</h2>
      <p className="text-text-secondary mb-4">
        The TCPA recognizes different levels of consent depending on the type of message you&apos;re
        sending:
      </p>

      <div className="grid grid-cols-1 gap-4 mb-6">
        <div className="bg-card-bg border border-border rounded-2xl px-5 py-3">
          <h4 className="font-semibold text-text-primary mb-2">Prior Express Consent</h4>
          <p className="text-sm text-text-secondary mb-3">
            The consumer has provided their phone number and consented to receive text messages.
            This can be verbal, written, or implied through the business relationship.
          </p>
          <p className="text-sm text-text-secondary m-0">
            <strong>Sufficient for:</strong> Informational/transactional messages (policy updates,
            payment reminders, appointment confirmations, claims status)
          </p>
        </div>

        <div className="bg-card-bg border border-border rounded-2xl px-5 py-3">
          <h4 className="font-semibold text-text-primary mb-2">Prior Express Written Consent</h4>
          <p className="text-sm text-text-secondary mb-3">
            A written agreement (paper or electronic) that clearly authorizes you to send text
            messages and includes specific required disclosures.
          </p>
          <p className="text-sm text-text-secondary m-0">
            <strong>Required for:</strong> Marketing/promotional messages (special offers,
            cross-sell campaigns, promotional content)
          </p>
        </div>
      </div>

      <div className="bg-brand/10 border border-brand/20 rounded-2xl px-5 py-3 mb-6">
        <h4 className="font-semibold text-text-primary mb-2">What Makes Written Consent Valid?</h4>
        <p className="text-sm text-text-primary mb-3">
          Under the TCPA, prior express written consent must:
        </p>
        <ul className="text-sm text-text-primary space-y-1 m-0">
          <li>• Be in writing (electronic signatures qualify)</li>
          <li>• Clearly authorize the sender to send text messages</li>
          <li>• Include the phone number being authorized</li>
          <li>• Disclose that the consumer may receive marketing messages</li>
          <li>• Disclose that consent is not a condition of purchase</li>
          <li>• Be signed by the consumer</li>
        </ul>
      </div>
    </section>
  );
}

// ── Message Categories & Obtaining Consent ──────────────────────────────────

function MessageCategoriesSection() {
  return (
    <section id="message-types" className="mb-12">
      <h2 className="text-2xl font-semibold mb-4 text-text-primary">
        Message Categories for Insurance Agencies
      </h2>
      <p className="text-text-secondary mb-4">
        Understanding which category your messages fall into determines what level of consent you
        need:
      </p>

      <h3 className="text-xl font-semibold mt-6 mb-3 text-text-primary">
        Transactional/Informational Messages
      </h3>
      <p className="text-text-secondary mb-2">
        These typically require only prior express consent (phone number + agreement to receive
        texts):
      </p>
      <ul className="list-disc pl-6 text-text-secondary mb-6 space-y-2">
        <li>Policy renewal reminders</li>
        <li>Payment due notices</li>
        <li>Claims status updates</li>
        <li>Appointment confirmations and reminders</li>
        <li>Policy change confirmations</li>
        <li>ID card delivery notifications</li>
        <li>Responses to client inquiries</li>
        <li>Coverage verification</li>
      </ul>

      <h3 className="text-xl font-semibold mt-6 mb-3 text-text-primary">
        Marketing/Promotional Messages
      </h3>
      <p className="text-text-secondary mb-2">These require prior express written consent:</p>
      <ul className="list-disc pl-6 text-text-secondary mb-6 space-y-2">
        <li>Cross-selling additional coverage</li>
        <li>New product announcements</li>
        <li>Special discounts or promotions</li>
        <li>Referral program invitations</li>
        <li>Holiday greetings with promotional content</li>
        <li>&quot;Bundle and save&quot; offers</li>
      </ul>

      <div className="bg-brand-yellow/15 border border-brand-yellow/40 rounded-2xl px-5 py-3 mb-6">
        <p className="text-sm text-text-primary m-0">
          <strong>Gray Area:</strong> Some messages blur the line between transactional and
          marketing. A renewal reminder that mentions &quot;this is a great time to review your
          coverage and potentially save&quot; could be considered marketing. When in doubt, err on
          the side of caution and obtain written consent.
        </p>
      </div>
    </section>
  );
}

function ObtainingConsentSection() {
  return (
    <section id="obtaining-consent" className="mb-12">
      <h2 className="text-2xl font-semibold mb-4 text-text-primary">How to Obtain Proper Consent</h2>

      <h3 className="text-xl font-semibold mt-6 mb-3 text-text-primary">For Transactional Messages</h3>
      <p className="text-text-secondary mb-4">You can obtain consent through:</p>
      <ul className="list-disc pl-6 text-text-secondary mb-6 space-y-2">
        <li>
          <strong>Application forms:</strong> Include a checkbox for text message consent when
          collecting client information
        </li>
        <li>
          <strong>Verbal consent:</strong> Ask clients if they&apos;d like to receive text updates
          and document the date and time
        </li>
        <li>
          <strong>Website/portal:</strong> Include text consent in online enrollment or account
          creation
        </li>
        <li>
          <strong>Initial text exchange:</strong> If a client texts you first, that can establish
          consent for ongoing transactional communication
        </li>
      </ul>

      <h3 className="text-xl font-semibold mt-6 mb-3 text-text-primary">For Marketing Messages</h3>
      <p className="text-text-secondary mb-4">Written consent must include specific language. Example:</p>

      <div className="bg-hover rounded-xl p-4 mb-6 text-sm text-text-primary border border-border">
        <p className="mb-3 italic">
          &quot;By providing your phone number and signing below, you consent to receive promotional
          text messages from [Agency Name] at this number. Message and data rates may apply. Message
          frequency varies. Reply STOP to unsubscribe at any time.{' '}
          <strong>Consent is not required as a condition of purchasing insurance.</strong>&quot;
        </p>
        <p className="m-0 font-medium">
          Phone number: _____________ Signature: _____________ Date: _______
        </p>
      </div>

      <h3 className="text-xl font-semibold mt-6 mb-3 text-text-primary">Document Everything</h3>
      <p className="text-text-secondary mb-4">Keep records that prove consent was obtained:</p>
      <ul className="list-disc pl-6 text-text-secondary mb-6 space-y-2">
        <li>Signed consent forms (physical or electronic)</li>
        <li>Timestamps of when consent was given</li>
        <li>The exact language the consumer agreed to</li>
        <li>Records of any subsequent opt-out requests</li>
      </ul>
    </section>
  );
}

// ── Opt-Out & DNC ───────────────────────────────────────────────────────────

function OptOutSection() {
  return (
    <section id="opt-out" className="mb-12">
      <h2 className="text-2xl font-semibold mb-4 text-text-primary">Opt-Out Requirements</h2>
      <p className="text-text-secondary mb-4">
        Under TCPA, you must provide a way for recipients to opt out, and you must honor opt-outs
        immediately.
      </p>

      <h3 className="text-xl font-semibold mt-6 mb-3 text-text-primary">Standard Opt-Out Keywords</h3>
      <p className="text-text-secondary mb-4">
        Carriers and industry standards recognize these keywords as opt-out requests:
      </p>
      <div className="bg-card-bg border border-border rounded-2xl px-5 py-3 mb-6">
        <div className="flex flex-wrap gap-2">
          <span className="px-3 py-1 bg-gray-100 rounded-full text-sm font-mono">STOP</span>
          <span className="px-3 py-1 bg-gray-100 rounded-full text-sm font-mono">UNSUBSCRIBE</span>
          <span className="px-3 py-1 bg-gray-100 rounded-full text-sm font-mono">CANCEL</span>
          <span className="px-3 py-1 bg-gray-100 rounded-full text-sm font-mono">END</span>
          <span className="px-3 py-1 bg-gray-100 rounded-full text-sm font-mono">QUIT</span>
        </div>
      </div>

      <h3 className="text-xl font-semibold mt-6 mb-3 text-text-primary">Best Practices for Opt-Outs</h3>
      <ul className="list-disc pl-6 text-text-secondary mb-6 space-y-2">
        <li>
          <strong>Immediate:</strong> Stop all messages as soon as an opt-out is received
        </li>
        <li>
          <strong>Confirmation:</strong> Send a single confirmation message acknowledging the
          opt-out
        </li>
        <li>
          <strong>No exceptions:</strong> Even transactional messages must stop after an opt-out
          (use other communication channels)
        </li>
        <li>
          <strong>Maintain records:</strong> Keep a list of opted-out numbers and check against it
          before sending
        </li>
      </ul>

      <div className="bg-brand-green/15 border border-brand-green/40 rounded-2xl px-5 py-3 mb-6">
        <p className="text-sm text-text-primary m-0">
          <strong>Voley automatically detects opt-out keywords</strong> and blocks further messages
          to that contact. You&apos;ll see an indicator that the contact has opted out. If they
          later want to opt back in, they can text START.
        </p>
      </div>
    </section>
  );
}

function DncRegistrySection() {
  return (
    <section id="dnc" className="mb-12">
      <h2 className="text-2xl font-semibold mb-4 text-text-primary">Do Not Call Registry</h2>
      <p className="text-text-secondary mb-4">
        The <strong>National Do Not Call (DNC) Registry</strong> is a separate regulation
        administered by the FTC. It primarily applies to telemarketing calls, but some state laws
        extend similar protections to text messages.
      </p>

      <div className="bg-red-50 border border-red-200 rounded-2xl px-5 py-3 mb-6">
        <h4 className="font-semibold text-red-900 mb-2">Your Responsibility</h4>
        <p className="text-sm text-red-800 m-0">
          <strong>Voley does not automatically scrub numbers against the DNC registry.</strong> You
          are responsible for checking numbers against the National DNC Registry and any applicable
          state DNC lists before sending marketing messages.
        </p>
      </div>

      <h3 className="text-xl font-semibold mt-6 mb-3 text-text-primary">When DNC Applies</h3>
      <ul className="list-disc pl-6 text-text-secondary mb-6 space-y-2">
        <li>
          <strong>Marketing messages:</strong> Check the DNC registry before sending promotional
          content
        </li>
        <li>
          <strong>Cold outreach:</strong> Always check before contacting prospects you don&apos;t
          have an existing relationship with
        </li>
      </ul>

      <h3 className="text-xl font-semibold mt-6 mb-3 text-text-primary">Exemptions</h3>
      <p className="text-text-secondary mb-4">
        The following may be exempt from DNC requirements (but consult legal counsel):
      </p>
      <ul className="list-disc pl-6 text-text-secondary mb-6 space-y-2">
        <li>
          <strong>Existing business relationship:</strong> You may contact customers you&apos;ve
          done business with in the past 18 months
        </li>
        <li>
          <strong>Inquiry exemption:</strong> You may contact someone who inquired about your
          services in the past 3 months
        </li>
        <li>
          <strong>Prior express consent:</strong> If someone specifically gave consent, DNC
          doesn&apos;t apply
        </li>
        <li>
          <strong>Purely informational:</strong> Non-marketing transactional messages are typically
          exempt
        </li>
      </ul>

      <h3 className="text-xl font-semibold mt-6 mb-3 text-text-primary">Accessing the DNC Registry</h3>
      <p className="text-text-secondary mb-4">
        You can access the National DNC Registry at{' '}
        <a
          href="https://telemarketing.donotcall.gov"
          target="_blank"
          rel="noopener noreferrer"
          className="text-brand hover:text-brand/80"
        >
          telemarketing.donotcall.gov
        </a>
        . Businesses must pay a fee to download the list and must download updated lists at least
        every 31 days.
      </p>
    </section>
  );
}

// ── State Laws ──────────────────────────────────────────────────────────────

function StateLawsSection() {
  return (
    <section id="state-laws" className="mb-12">
      <h2 className="text-2xl font-semibold mb-4 text-text-primary">State Laws & Regulations</h2>
      <p className="text-text-secondary mb-4">
        Many states have their own telemarketing and consumer protection laws that may be{' '}
        <strong>more restrictive</strong> than federal TCPA requirements.
      </p>

      <h3 className="text-xl font-semibold mt-6 mb-3 text-text-primary">
        States with Notable SMS/Telemarketing Laws
      </h3>
      <div className="bg-card-bg border border-border rounded-2xl overflow-hidden mb-6">
        <table className="w-full text-sm">
          <thead className="bg-hover">
            <tr>
              <th className="px-4 py-3 text-left font-semibold text-text-primary">State</th>
              <th className="px-4 py-3 text-left font-semibold text-text-primary">
                Notable Requirements
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            <tr>
              <td className="px-4 py-3 text-text-secondary font-medium">California</td>
              <td className="px-4 py-3 text-text-secondary">
                Automatic DNC registration with cell phone purchase; CCPA privacy considerations
              </td>
            </tr>
            <tr>
              <td className="px-4 py-3 text-text-secondary font-medium">Florida</td>
              <td className="px-4 py-3 text-text-secondary">
                Requires written consent for ALL commercial text messages (effective 7/1/2021)
              </td>
            </tr>
            <tr>
              <td className="px-4 py-3 text-text-secondary font-medium">Oklahoma</td>
              <td className="px-4 py-3 text-text-secondary">
                Requires written consent for commercial texts; specific insurance solicitation rules
              </td>
            </tr>
            <tr>
              <td className="px-4 py-3 text-text-secondary font-medium">Maryland</td>
              <td className="px-4 py-3 text-text-secondary">
                Express written consent required for all commercial texts
              </td>
            </tr>
            <tr>
              <td className="px-4 py-3 text-text-secondary font-medium">Washington</td>
              <td className="px-4 py-3 text-text-secondary">
                State DNC list; additional consumer protection requirements
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div className="bg-brand-yellow/15 border border-brand-yellow/40 rounded-2xl px-5 py-3 mb-6">
        <p className="text-sm text-text-primary m-0">
          <strong>Insurance-Specific Regulations:</strong> Many states have specific rules about
          insurance solicitation and marketing that may affect your texting practices. Check with
          your state insurance department and legal counsel to understand requirements in your
          operating states.
        </p>
      </div>
    </section>
  );
}

// ── How Voley Helps ─────────────────────────────────────────────────────────

function VoleyToolsSection() {
  return (
    <section id="voley-tools" className="mb-12">
      <h2 className="text-2xl font-semibold mb-4 text-text-primary">
        How Voley Helps You Stay Compliant
      </h2>
      <p className="text-text-secondary mb-4">
        Voley provides several features to support your compliance efforts. However, these tools are
        assistive — they don&apos;t transfer legal responsibility and don&apos;t guarantee
        compliance.
      </p>

      <div className="grid grid-cols-1 gap-4 mb-6">
        <div className="bg-card-bg border border-border rounded-2xl px-5 py-3">
          <h4 className="font-semibold text-text-primary mb-2">Consent Tracking</h4>
          <p className="text-sm text-text-secondary m-0">
            Track when contacts were added and how they consented to receive messages. Record
            consent sources and maintain audit trails.
          </p>
        </div>

        <div className="bg-card-bg border border-border rounded-2xl px-5 py-3">
          <h4 className="font-semibold text-text-primary mb-2">Automatic Opt-Out Detection</h4>
          <p className="text-sm text-text-secondary m-0">
            Voley automatically detects standard opt-out keywords (STOP, UNSUBSCRIBE, etc.) and
            blocks further messages to that contact. Opt-out status is clearly visible in the
            contact record.
          </p>
        </div>

        <div className="bg-card-bg border border-border rounded-2xl px-5 py-3">
          <h4 className="font-semibold text-text-primary mb-2">Message Guardrails</h4>
          <p className="text-sm text-text-secondary m-0">
            Configure compliance warnings that alert users when messages contain potentially
            problematic content (coverage guarantees, binding language, etc.). Users can still send
            but must acknowledge the warning.
          </p>
        </div>

        <div className="bg-card-bg border border-border rounded-2xl px-5 py-3">
          <h4 className="font-semibold text-text-primary mb-2">Complete Message Archive</h4>
          <p className="text-sm text-text-secondary m-0">
            All messages are retained with timestamps, supporting audit and compliance review
            requirements. Access historical conversations when needed.
          </p>
        </div>

        <div className="bg-card-bg border border-border rounded-2xl px-5 py-3">
          <h4 className="font-semibold text-text-primary mb-2">Sender Identification</h4>
          <p className="text-sm text-text-secondary m-0">
            Messages are sent from your agency&apos;s registered phone number, and templates can
            include agency identification to meet disclosure requirements.
          </p>
        </div>

        <div className="bg-card-bg border border-border rounded-2xl px-5 py-3">
          <h4 className="font-semibold text-text-primary mb-2">A2P 10DLC Registration</h4>
          <p className="text-sm text-text-secondary m-0">
            Voley handles carrier registration for your phone number, which is required by carriers
            but is separate from TCPA consent requirements. See our{' '}
            <Link href="/docs/compliance" className="text-brand hover:text-brand/80">
              A2P 10DLC guide
            </Link>
            .
          </p>
        </div>
      </div>

      <div className="bg-red-50 border border-red-200 rounded-2xl px-5 py-3 mb-6">
        <h4 className="font-semibold text-red-900 mb-2">What Voley Does NOT Do</h4>
        <ul className="text-sm text-red-800 space-y-1 m-0">
          <li>
            • <strong>Verify that your consent is valid</strong> — you must ensure consent meets
            TCPA requirements
          </li>
          <li>
            • <strong>Scrub numbers against the DNC registry</strong> — this is your responsibility
          </li>
          <li>
            • <strong>Determine if a message is transactional or marketing</strong> — you must
            categorize your messages
          </li>
          <li>
            • <strong>Ensure state-specific compliance</strong> — you must know the laws in your
            operating states
          </li>
          <li>
            • <strong>Provide legal advice</strong> — consult qualified legal counsel
          </li>
        </ul>
      </div>
    </section>
  );
}

// ── Best Practices ──────────────────────────────────────────────────────────

function BestPracticesSection() {
  return (
    <section id="best-practices" className="mb-12">
      <h2 className="text-2xl font-semibold mb-4 text-text-primary">
        Best Practices for Compliant Messaging
      </h2>

      <h3 className="text-xl font-semibold mt-6 mb-3 text-text-primary">
        1. Get Consent First, Message Second
      </h3>
      <p className="text-text-secondary mb-4">
        Never assume consent. Have a documented process for obtaining consent and always verify
        consent status before sending messages to a contact for the first time.
      </p>

      <h3 className="text-xl font-semibold mt-6 mb-3 text-text-primary">
        2. Keep Transactional and Marketing Separate
      </h3>
      <p className="text-text-secondary mb-4">
        Be intentional about categorizing your messages. Don&apos;t sneak marketing content into
        transactional messages. If you want to market, obtain the proper written consent.
      </p>

      <h3 className="text-xl font-semibold mt-6 mb-3 text-text-primary">3. Always Identify Yourself</h3>
      <p className="text-text-secondary mb-4">
        Include your agency name in messages so recipients know who&apos;s contacting them:
      </p>
      <div className="bg-hover rounded-xl p-4 mb-6 font-mono text-sm text-text-primary">
        &quot;Hi John, this is Sarah from ABC Insurance. Your auto policy renewal documents are
        ready...&quot;
      </div>

      <h3 className="text-xl font-semibold mt-6 mb-3 text-text-primary">
        4. Respect Time Restrictions
      </h3>
      <p className="text-text-secondary mb-4">
        Don&apos;t send messages before 8 AM or after 9 PM in the recipient&apos;s time zone. Be
        mindful of clients in different time zones than your office.
      </p>

      <h3 className="text-xl font-semibold mt-6 mb-3 text-text-primary">
        5. Include Opt-Out Instructions
      </h3>
      <p className="text-text-secondary mb-4">
        Particularly for marketing messages, include opt-out instructions:
      </p>
      <div className="bg-hover rounded-xl p-4 mb-6 font-mono text-sm text-text-primary">
        &quot;...Reply STOP to opt out of marketing messages.&quot;
      </div>

      <h3 className="text-xl font-semibold mt-6 mb-3 text-text-primary">6. Document and Audit</h3>
      <p className="text-text-secondary mb-4">
        Keep records of consent, opt-outs, and messaging activities. Periodically audit your
        practices to ensure compliance.
      </p>

      <h3 className="text-xl font-semibold mt-6 mb-3 text-text-primary">7. Train Your Team</h3>
      <p className="text-text-secondary mb-4">
        Everyone who sends messages on behalf of your agency should understand these requirements. A
        single team member&apos;s violation exposes the entire organization.
      </p>
    </section>
  );
}

// ── Insurance-Specific Considerations ───────────────────────────────────────

function InsuranceSpecificSection() {
  return (
    <section id="insurance-specific" className="mb-12">
      <h2 className="text-2xl font-semibold mb-4 text-text-primary">
        Insurance-Specific Considerations
      </h2>

      <h3 className="text-xl font-semibold mt-6 mb-3 text-text-primary">
        Common Insurance Messaging Scenarios
      </h3>

      <div className="bg-card-bg border border-border rounded-2xl overflow-hidden mb-6">
        <table className="w-full text-sm">
          <thead className="bg-hover">
            <tr>
              <th className="px-4 py-3 text-left font-semibold text-text-primary">Scenario</th>
              <th className="px-4 py-3 text-left font-semibold text-text-primary">Likely Category</th>
              <th className="px-4 py-3 text-left font-semibold text-text-primary">Consent Needed</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            <tr>
              <td className="px-4 py-3 text-text-secondary">Policy renewal reminder</td>
              <td className="px-4 py-3 text-text-secondary">Transactional</td>
              <td className="px-4 py-3 text-text-secondary">Prior express consent</td>
            </tr>
            <tr>
              <td className="px-4 py-3 text-text-secondary">Payment due notice</td>
              <td className="px-4 py-3 text-text-secondary">Transactional</td>
              <td className="px-4 py-3 text-text-secondary">Prior express consent</td>
            </tr>
            <tr>
              <td className="px-4 py-3 text-text-secondary">Claims status update</td>
              <td className="px-4 py-3 text-text-secondary">Transactional</td>
              <td className="px-4 py-3 text-text-secondary">Prior express consent</td>
            </tr>
            <tr>
              <td className="px-4 py-3 text-text-secondary">Appointment confirmation</td>
              <td className="px-4 py-3 text-text-secondary">Transactional</td>
              <td className="px-4 py-3 text-text-secondary">Prior express consent</td>
            </tr>
            <tr>
              <td className="px-4 py-3 text-text-secondary">&quot;Bundle and save&quot; offer</td>
              <td className="px-4 py-3 text-text-secondary">Marketing</td>
              <td className="px-4 py-3 text-text-secondary">Prior express written consent</td>
            </tr>
            <tr>
              <td className="px-4 py-3 text-text-secondary">New product announcement</td>
              <td className="px-4 py-3 text-text-secondary">Marketing</td>
              <td className="px-4 py-3 text-text-secondary">Prior express written consent</td>
            </tr>
            <tr>
              <td className="px-4 py-3 text-text-secondary">Referral request</td>
              <td className="px-4 py-3 text-text-secondary">Marketing</td>
              <td className="px-4 py-3 text-text-secondary">Prior express written consent</td>
            </tr>
            <tr>
              <td className="px-4 py-3 text-text-secondary">Policy review invitation</td>
              <td className="px-4 py-3 text-text-secondary">Gray area*</td>
              <td className="px-4 py-3 text-text-secondary">Written recommended</td>
            </tr>
          </tbody>
        </table>
      </div>

      <p className="text-text-secondary mb-4 text-sm">
        *Policy review invitations can be transactional (service-related) or marketing (upsell
        opportunity) depending on context and wording. When in doubt, treat as marketing.
      </p>

      <h3 className="text-xl font-semibold mt-6 mb-3 text-text-primary">Sensitive Information</h3>
      <div className="bg-red-50 border border-red-200 rounded-2xl px-5 py-3 mb-6">
        <p className="text-sm text-red-800 mb-3">
          <strong>Do NOT send via text:</strong>
        </p>
        <ul className="text-sm text-red-800 space-y-1 m-0">
          <li>• Protected Health Information (PHI) — Voley is not HIPAA compliant</li>
          <li>• Full Social Security numbers</li>
          <li>• Complete credit card or bank account numbers</li>
          <li>• Detailed medical information</li>
          <li>• Login credentials or passwords</li>
        </ul>
      </div>
    </section>
  );
}

// ── Penalties ───────────────────────────────────────────────────────────────

function PenaltiesSection() {
  return (
    <section id="penalties" className="mb-12">
      <h2 className="text-2xl font-semibold mb-4 text-text-primary">Penalties for Non-Compliance</h2>

      <h3 className="text-xl font-semibold mt-6 mb-3 text-text-primary">TCPA Statutory Damages</h3>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
        <div className="bg-card-bg border border-border rounded-2xl px-5 py-3">
          <h4 className="font-semibold text-text-primary mb-2">$500 per violation</h4>
          <p className="text-sm text-text-secondary m-0">
            For negligent violations. Each text message to each recipient is a separate violation.
          </p>
        </div>
        <div className="bg-card-bg border border-border rounded-2xl px-5 py-3">
          <h4 className="font-semibold text-text-primary mb-2">$1,500 per violation</h4>
          <p className="text-sm text-text-secondary m-0">
            For willful or knowing violations (treble damages). Courts can assess this for continued
            violations after receiving complaints.
          </p>
        </div>
      </div>

      <h3 className="text-xl font-semibold mt-6 mb-3 text-text-primary">Additional Consequences</h3>
      <ul className="list-disc pl-6 text-text-secondary mb-6 space-y-2">
        <li>
          <strong>Class action lawsuits:</strong> TCPA violations commonly result in class actions
          with thousands or millions of class members
        </li>
        <li>
          <strong>State AG enforcement:</strong> State attorneys general can bring actions under
          state consumer protection laws
        </li>
        <li>
          <strong>Carrier suspension:</strong> Your phone number or messaging capabilities may be
          suspended
        </li>
        <li>
          <strong>FCC enforcement:</strong> The FCC can investigate complaints and impose additional
          penalties
        </li>
      </ul>

      <div className="bg-brand-yellow/15 border border-brand-yellow/40 rounded-2xl px-5 py-3 mb-6">
        <p className="text-sm text-text-primary m-0">
          <strong>Prevention is far less expensive than defense.</strong> Implementing proper
          compliance practices costs a fraction of what a single TCPA lawsuit could cost in damages,
          legal fees, and settlement.
        </p>
      </div>
    </section>
  );
}

// ── Compliance Checklist ────────────────────────────────────────────────────

function ChecklistSection() {
  return (
    <section id="checklist" className="mb-12">
      <h2 className="text-2xl font-semibold mb-4 text-text-primary">TCPA Compliance Checklist</h2>
      <p className="text-text-secondary mb-4">Use this checklist to evaluate your current practices:</p>

      <div className="bg-card-bg border border-border rounded-2xl px-5 py-4 mb-6">
        <h4 className="font-semibold text-text-primary mb-4">Consent</h4>
        <div className="space-y-3">
          <label className="flex items-start">
            <input type="checkbox" className="mt-1 mr-3 h-4 w-4 rounded border-gray-300" />
            <span className="text-sm text-text-secondary">
              We have a process for obtaining consent before texting contacts
            </span>
          </label>
          <label className="flex items-start">
            <input type="checkbox" className="mt-1 mr-3 h-4 w-4 rounded border-gray-300" />
            <span className="text-sm text-text-secondary">
              For marketing messages, we obtain prior express written consent with required
              disclosures
            </span>
          </label>
          <label className="flex items-start">
            <input type="checkbox" className="mt-1 mr-3 h-4 w-4 rounded border-gray-300" />
            <span className="text-sm text-text-secondary">
              We document when and how consent was obtained
            </span>
          </label>
          <label className="flex items-start">
            <input type="checkbox" className="mt-1 mr-3 h-4 w-4 rounded border-gray-300" />
            <span className="text-sm text-text-secondary">
              Our consent forms include &quot;consent is not required for purchase&quot; language
            </span>
          </label>
        </div>
      </div>

      <div className="bg-card-bg border border-border rounded-2xl px-5 py-4 mb-6">
        <h4 className="font-semibold text-text-primary mb-4">Opt-Out</h4>
        <div className="space-y-3">
          <label className="flex items-start">
            <input type="checkbox" className="mt-1 mr-3 h-4 w-4 rounded border-gray-300" />
            <span className="text-sm text-text-secondary">We honor opt-out requests immediately</span>
          </label>
          <label className="flex items-start">
            <input type="checkbox" className="mt-1 mr-3 h-4 w-4 rounded border-gray-300" />
            <span className="text-sm text-text-secondary">
              We maintain a suppression list of opted-out numbers
            </span>
          </label>
          <label className="flex items-start">
            <input type="checkbox" className="mt-1 mr-3 h-4 w-4 rounded border-gray-300" />
            <span className="text-sm text-text-secondary">
              Marketing messages include opt-out instructions
            </span>
          </label>
        </div>
      </div>

      <div className="bg-card-bg border border-border rounded-2xl px-5 py-4 mb-6">
        <h4 className="font-semibold text-text-primary mb-4">Message Content</h4>
        <div className="space-y-3">
          <label className="flex items-start">
            <input type="checkbox" className="mt-1 mr-3 h-4 w-4 rounded border-gray-300" />
            <span className="text-sm text-text-secondary">Messages identify our agency by name</span>
          </label>
          <label className="flex items-start">
            <input type="checkbox" className="mt-1 mr-3 h-4 w-4 rounded border-gray-300" />
            <span className="text-sm text-text-secondary">
              We distinguish between transactional and marketing messages
            </span>
          </label>
          <label className="flex items-start">
            <input type="checkbox" className="mt-1 mr-3 h-4 w-4 rounded border-gray-300" />
            <span className="text-sm text-text-secondary">
              We don&apos;t send sensitive information (SSN, PHI, etc.) via text
            </span>
          </label>
        </div>
      </div>

      <div className="bg-card-bg border border-border rounded-2xl px-5 py-4 mb-6">
        <h4 className="font-semibold text-text-primary mb-4">DNC & Records</h4>
        <div className="space-y-3">
          <label className="flex items-start">
            <input type="checkbox" className="mt-1 mr-3 h-4 w-4 rounded border-gray-300" />
            <span className="text-sm text-text-secondary">
              We check the DNC registry before sending marketing messages to new contacts
            </span>
          </label>
          <label className="flex items-start">
            <input type="checkbox" className="mt-1 mr-3 h-4 w-4 rounded border-gray-300" />
            <span className="text-sm text-text-secondary">
              We maintain an internal DNC list and check it before messaging
            </span>
          </label>
          <label className="flex items-start">
            <input type="checkbox" className="mt-1 mr-3 h-4 w-4 rounded border-gray-300" />
            <span className="text-sm text-text-secondary">
              We retain records of consent, opt-outs, and messages
            </span>
          </label>
        </div>
      </div>

      <div className="bg-card-bg border border-border rounded-2xl px-5 py-4 mb-6">
        <h4 className="font-semibold text-text-primary mb-4">Training & Policy</h4>
        <div className="space-y-3">
          <label className="flex items-start">
            <input type="checkbox" className="mt-1 mr-3 h-4 w-4 rounded border-gray-300" />
            <span className="text-sm text-text-secondary">
              Team members are trained on TCPA requirements
            </span>
          </label>
          <label className="flex items-start">
            <input type="checkbox" className="mt-1 mr-3 h-4 w-4 rounded border-gray-300" />
            <span className="text-sm text-text-secondary">We have a written SMS compliance policy</span>
          </label>
          <label className="flex items-start">
            <input type="checkbox" className="mt-1 mr-3 h-4 w-4 rounded border-gray-300" />
            <span className="text-sm text-text-secondary">
              We periodically audit our messaging practices
            </span>
          </label>
        </div>
      </div>
    </section>
  );
}

// ── Additional Resources & Need Help ────────────────────────────────────────

function AdditionalResourcesSection({ external }: { external: boolean }) {
  return (
    <>
      <section className="mb-12">
        <h2 className="text-2xl font-semibold mb-4 text-text-primary">Additional Resources</h2>
        <p className="text-text-secondary mb-4">For more information on messaging compliance:</p>
        <ul className="list-disc pl-6 text-text-secondary mb-6 space-y-2">
          <li>
            <Link href="/docs/sms-best-practices" className="text-brand hover:text-brand/80">
              SMS Best Practices Guide
            </Link>{' '}
            — Practical tips for effective, compliant messaging
          </li>
          <li>
            <Link href="/docs/compliance" className="text-brand hover:text-brand/80">
              A2P 10DLC Compliance Guide
            </Link>{' '}
            — Carrier registration requirements
          </li>
          <li>
            <a
              href="https://www.fcc.gov/consumers/guides/stop-unwanted-robocalls-and-texts"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand hover:text-brand/80"
            >
              FCC Consumer Guide
            </a>{' '}
            — Federal Communications Commission resources
          </li>
          <li>
            <a
              href="https://telemarketing.donotcall.gov"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand hover:text-brand/80"
            >
              National Do Not Call Registry
            </a>{' '}
            — FTC&apos;s DNC registration site
          </li>
        </ul>
      </section>

      <section className="mb-12">
        <h2 className="text-2xl font-semibold mb-4 text-text-primary">Need Help?</h2>
        <p className="text-text-secondary mb-4">
          If you have questions about how Voley can support your compliance efforts, or need help
          configuring compliance features, we&apos;re here to help.
        </p>
        <div className="flex flex-col sm:flex-row gap-4">
          <Link
            href={external ? '/contact' : '/help?tab=support'}
            className="inline-flex items-center justify-center px-6 py-3 border-0 text-base font-medium rounded-full text-white bg-primary hover:shadow-lg transition-all hover:scale-[1.01] no-underline"
          >
            Contact Support
          </Link>
          <Link
            href={external ? '/docs/compliance' : '/help?tab=guides&doc=compliance'}
            className="inline-flex items-center justify-center px-6 py-3 border border-border text-base font-medium rounded-full text-text-primary bg-card-bg hover:bg-hover hover:border-gray-300 transition-all no-underline"
          >
            A2P 10DLC Guide →
          </Link>
        </div>
      </section>
    </>
  );
}

/* ---------------------------------------------------------------------------
 * Main composer
 * --------------------------------------------------------------------------- */

interface TcpaGuideContentProps {
  className?: string;
  /** 'external' points CTA links at marketing routes instead of in-app help routes */
  variant?: 'app' | 'external';
}

export default function TcpaGuideContent({ className, variant = 'app' }: TcpaGuideContentProps) {
  return (
    <div className={className}>
      <WhatIsTcpaSection />
      <WhyComplianceMattersSection />
      <ConsentTypesSection />
      <MessageCategoriesSection />
      <ObtainingConsentSection />
      <OptOutSection />
      <DncRegistrySection />
      <StateLawsSection />
      <VoleyToolsSection />
      <BestPracticesSection />
      <InsuranceSpecificSection />
      <PenaltiesSection />
      <ChecklistSection />
      <AdditionalResourcesSection external={variant === 'external'} />
    </div>
  );
}
